import { promisedComputed } from "computed-async-mobx";
import { computed, observable } from "mobx";
import { AnalyticsSeriesEnum, MediaAnalyticsApi } from "app/backend/mediaAnalyticsApi";
import { ChartHistogramProvider } from "app/models/chartHistogram/chartHistogramProvider";
import { TimeUtils } from "app/utils/timeUtils";
import { ChartHistogramSeries } from "app/models/chartHistogram/chartHistogram";
import { Fonts } from "app/styling/fonts";
import { UnitKindEnum, unitsFormatter } from "app/utils/unitsFormatter";
import dayjs from "dayjs-ext";
import { StoreStatus } from "app/stores/_models/storeStatus";
import { now } from "mobx-utils";
import * as _ from "lodash";

export class MainStore {
  @observable
  status: StoreStatus = new StoreStatus();

  private calculatePercent(value: number, total: number): number {
    let percentValue = 0;
    if (total > 0) {
      percentValue = (value / total) * 100;
    }
    const percision = percentValue % 1 === 0 ? 0 : 1;
    return Number(percentValue.toFixed(percision));
  }

  private hitRateHistogramAsync = promisedComputed(undefined, async () => {
    const mediaAnalytics = new MediaAnalyticsApi();
    const chartHistogramProvider = new ChartHistogramProvider(mediaAnalytics);

    try {
      const histogram = await chartHistogramProvider.provide(
        TimeUtils.createConfig(
          TimeUtils.DAY,
          TimeUtils.getBinIntervalForPeriod(TimeUtils.DAY),
          now(1000 * TimeUtils.MINUTE * 15)
        ),
        [AnalyticsSeriesEnum.TPS_DELIVERY_PER_SITE, AnalyticsSeriesEnum.TPS_ORIGIN_PER_SITE],
        {
          totals: "allSites",
        }
      );
      const totalTransactions = _.sumBy(histogram, seriesModel => seriesModel.total);
      const hitTransactions = histogram.find(series => series.series === AnalyticsSeriesEnum.TPS_DELIVERY_PER_SITE)!
        .total;

      return this.calculatePercent(hitTransactions, totalTransactions);
    } catch (e) {
      this.status.setError("failed to fetch bandwidth data", e);
    }
  });

  private bandwidthHistogramAsync = promisedComputed(undefined, async () => {
    const mediaAnalytics = new MediaAnalyticsApi();
    const chartHistogramProvider = new ChartHistogramProvider(mediaAnalytics);

    try {
      const histogram = await chartHistogramProvider.provide(
        TimeUtils.createConfig(
          TimeUtils.DAY,
          TimeUtils.getBinIntervalForPeriod(TimeUtils.DAY),
          now(1000 * TimeUtils.MINUTE * 15)
        ),
        [AnalyticsSeriesEnum.BANDWIDTH_BY_QWILT]
      );
      return histogram[0];
    } catch (e) {
      this.status.setError("failed to fetch bandwidth data", e);
    }
  });

  private volumeHistogramAsync = promisedComputed(undefined, async () => {
    const mediaAnalytics = new MediaAnalyticsApi();
    const chartHistogramProvider = new ChartHistogramProvider(mediaAnalytics);

    try {
      const histogram = await chartHistogramProvider.provide(
        TimeUtils.createConfig(
          TimeUtils.DAY,
          TimeUtils.getBinIntervalForPeriod(TimeUtils.DAY),
          now(1000 * TimeUtils.MINUTE * 15)
        ),
        [AnalyticsSeriesEnum.VOLUME_SERVED_QWILT]
      );
      return histogram[0];
    } catch (e) {
      this.status.setError("failed to fetch bandwidth data", e);
    }
  });

  private qoeHistogramAsync = promisedComputed(undefined, async () => {
    const mediaAnalytics = new MediaAnalyticsApi();
    const chartHistogramProvider = new ChartHistogramProvider(mediaAnalytics);

    try {
      const histogram = await chartHistogramProvider.provide(
        TimeUtils.createConfig(
          TimeUtils.DAY,
          TimeUtils.getBinIntervalForPeriod(TimeUtils.DAY, TimeUtils.HOUR),
          now(1000 * TimeUtils.HOUR)
        ),
        [AnalyticsSeriesEnum.QOE_SERVED_BITRATE]
      );
      return histogram[0];
    } catch (e) {
      this.status.setError("failed to fetch bandwidth data", e);
    }
  });

  @computed
  get hitRatePercent(): number | undefined {
    const ratio = this.hitRateHistogramAsync.get();
    if (ratio) {
      return ratio;
    }
  }

  @computed
  get qoeHistogram(): ChartHistogramSeries | undefined {
    const histogram = this.qoeHistogramAsync.get();
    if (histogram) {
      return histogram;
    }
  }

  @computed
  get bandwidthHistogram(): ChartHistogramSeries | undefined {
    const histogram = this.bandwidthHistogramAsync.get();
    if (histogram) {
      return histogram;
    }
  }

  @computed
  get volumeHistogram(): ChartHistogramSeries | undefined {
    const histogram = this.volumeHistogramAsync.get();
    if (histogram) {
      return histogram;
    }
  }

  @computed
  get bandwidthChartOptions(): any {
    return {
      series: this.bandwidthHistogram
        ? [
            {
              name: "Bandwidth",
              data: this.bandwidthHistogram.getAll,
              color: "#bdff00",
              id: AnalyticsSeriesEnum.BANDWIDTH_BY_QWILT,
            },
          ]
        : [],
      title: "",
      chart: {
        marginRight: 30,
        marginLeft: 30,
        spacingLeft: 0,
        spacingRight: 0,
        spacingTop: 30,

        zoomType: "x",

        type: "area",
        backgroundColor: "#022e3f",
      },

      time: {
        useUTC: false,
      },

      plotOptions: {
        series: {
          lineWidth: 2,
          fillOpacity: 0.25,
          marker: {
            enabled: false,
          },
        },
      },

      legend: {
        enabled: false,
      },

      credits: {
        enabled: false,
      },

      yAxis: [
        {
          showFirstLabel: false,
          gridLineWidth: 1,
          gridLineColor: "rgba(234, 249, 255, 0.3)",
          title: {
            enabled: false,
          },
          labels: {
            align: "left",
            x: -20,
            style: {
              fontFamily: Fonts.FONT_FAMILY,
              fontSize: "16px",
              color: "white",
              opacity: 0.8,
              width: 75,
            },
            formatter(): string {
              const data = this as any;

              const formatted = unitsFormatter.format(data.value, UnitKindEnum.TRAFFIC);
              const value =
                formatted.formattedNumber % 1 === 0 ? formatted.formattedNumber.toFixed() : formatted.formattedNumber;
              return `${value} ${data.isLast ? formatted.unit : ""}`;
            },
          },
        },
      ],

      xAxis: {
        minPadding: 0,
        maxPadding: 0,
        showFirstLabel: true,
        type: "datetime",
        gridLineWidth: 1,
        gridLineColor: "rgba(234, 249, 255, 0.3)",
        labels: {
          padding: 0,
          autoRotationLimit: 0,
          overflow: "allow",
          style: {
            fontSize: "16px",
            maxWidth: "24px",
            textOverflow: "ellipsis",
            color: "white",
            opacity: 0.4,
          },
          formatter(): string {
            const data = this as any;
            const date = dayjs(data.value).local();
            const defaultLabel = data.axis.defaultLabelFormatter.call(data);
            let showDate = !defaultLabel.includes(":");
            const text = showDate ? `<b>${TimeUtils.parseMonth(date.month())} ${date.date()}</b>` : defaultLabel;
            return `<span style="font-family: ${Fonts.FONT_FAMILY};">${text}</span>`;
          },
        },
        dateTimeLabelFormats: {
          day: "%e/%b",
          week: "%e/%b",
        },
      },

      tooltip: false,
    };
  }
}



// WEBPACK FOOTER //
// ./app/stores/mainStore.ts